import {
  isRejectedWithValue,
  isPending,
  isFulfilled,
  Middleware,
  MiddlewareAPI
} from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import { toast } from 'sonner';
import * as Sentry from '@sentry/react';

interface RtkQueryError {
  status: number;
  data?: {
    message?: string;
    error?: string | {
      status?: number;
      name?: string;
      message?: string;
      details?: any;
    };
    detail?: string;
  };
  message?: string;
}

/**
 * Safely extract error message string from various error formats
 */
const extractErrorMessage = (payload: any): string => {
  if (!payload) return 'An error occurred';

  // Handle Strapi v4/v5 error format: { data: { error: { message, status, name, details } } }
  if (payload.data?.error) {
    const errorObj = payload.data.error;
    
    // If error is an object with message property
    if (typeof errorObj === 'object' && errorObj !== null) {
      if (errorObj.message) return String(errorObj.message);
      if (errorObj.name) return String(errorObj.name);
    }
    
    // If error is a string
    if (typeof errorObj === 'string') return errorObj;
  }

  // Handle standard error formats
  if (payload.data?.message) return String(payload.data.message);
  if (payload.data?.detail) return String(payload.data.detail);
  if (payload.message) return String(payload.message);
  
  // If payload itself is a string
  if (typeof payload === 'string') return payload;

  // Fallback
  return 'An error occurred';
};

export const errorLogger: Middleware =
  (api: MiddlewareAPI) => (next) => (action) => {
    // Handle pending actions (optional - for loading states)
    if (isPending(action)) {
      // You can add loading logic here if needed
      // console.log('Pending:', action.type);
    }

    // Handle fulfilled actions (optional - for success messages)
    if (isFulfilled(action)) {
      // You can add success logic here if needed
      // console.log('Fulfilled:', action.type);
    }

    // Handle rejected actions
    if (isRejectedWithValue(action)) {
      const typedAction = action as PayloadAction<RtkQueryError>;
      let errorMessage = 'An error occurred';
      let statusCode: number | undefined;

      // Extract error information
      if (typedAction.payload) {
        statusCode = typedAction.payload.status;
        
        // ✅ Use safe error extraction function
        errorMessage = extractErrorMessage(typedAction.payload);
      } else if (action.error?.message) {
        errorMessage = String(action.error.message);
      }

      // ✅ Final safety check - ensure errorMessage is always a string
      if (typeof errorMessage !== 'string') {
        console.error('Error message is not a string:', errorMessage);
        errorMessage = 'An unexpected error occurred';
      }

      // Handle specific HTTP status codes
      switch (statusCode) {
        case 400:
          toast.error('Bad Request', {
            description: errorMessage,
            duration: 5000,
          });
          break;
        case 401:
          toast.error('Unauthorized', {
            description: 'Please login again',
            duration: 5000,
          });
          // Optional: Redirect to login after a delay
          // setTimeout(() => { window.location.href = '/signin'; }, 2000);
          break;
        case 403:
          toast.error('Forbidden', {
            description: "You don't have permission to perform this action",
            duration: 5000,
          });
          break;
        case 404:
          toast.error('Not Found', {
            description: errorMessage,
            duration: 5000,
          });
          break;
        case 422:
          toast.error('Validation Error', {
            description: errorMessage,
            duration: 5000,
          });
          break;
        case 429:
          toast.error('Too Many Requests', {
            description: 'Please slow down and try again later',
            duration: 5000,
          });
          break;
        case 500:
          toast.error('Server Error', {
            description: 'Please try again later',
            duration: 5000,
          });
          break;
        case 502:
        case 503:
        case 504:
          toast.error('Service Unavailable', {
            description: 'The service is temporarily unavailable',
            duration: 5000,
          });
          break;
        default:
          // For non-specific errors, just show the message
          toast.error('Error', {
            description: errorMessage,
            duration: 5000,
          });
      }

      // Development logging
      if (import.meta.env.DEV) {
        console.group('🔴 Redux Error');
        console.log('Action:', action.type);
        console.log('Status:', statusCode);
        console.log('Message:', errorMessage);
        console.log('Full Payload:', typedAction.payload);
        console.log('Error Object:', action.error);
        console.groupEnd();
      }

      // Sentry reporting in production
      if (import.meta.env.PROD) {
        Sentry.captureException(new Error(errorMessage), {
          level: statusCode && statusCode >= 500 ? 'error' : 'warning',
          extra: {
            actionType: action.type,
            statusCode,
            payload: typedAction.payload,
            error: action.error,
            extractedMessage: errorMessage,
          },
          tags: {
            errorType: 'redux-query-error',
            httpStatus: statusCode?.toString() || 'unknown',
          },
        });
      }
    }

    // ✅ ALWAYS RETURN next(action)
    return next(action);
  };
